import { omit, omitBy, uniq, without } from 'lodash';
import { ACTION_TYPES as TASKS_ACTION_TYPES } from '../../tasks/constants';
import { ACTION_TYPES as SECTIONS_ACTION_TYPES } from '../../sections/constants';

const defaultState = {};

const infiniteListDataReducer = (state = defaultState, action) => {
  switch (action.type) {
    case TASKS_ACTION_TYPES.GET_TASKS_REQUEST:
      return defaultState;
    case TASKS_ACTION_TYPES.GET_TASKS_INFINITE_LIST_FIRST_PAGE_SUCCESS:
      return {
        ...state,
        ...action.payload,
      };
    case TASKS_ACTION_TYPES.GET_TASKS_INFINITE_LIST_NEXT_PAGE_REQUEST:
      if (action.payload.projectId !== state.projectId) {
        return state;
      }
      return {
        ...state,
        loadingList: uniq([
          ...(state?.loadingList || []),
          ...action?.payload?.loadingList,
        ]),
      };
    case TASKS_ACTION_TYPES.GET_TASKS_INFINITE_LIST_NEXT_PAGE_SUCCESS:
      if (action.payload.projectId !== state.projectId) {
        return state;
      }
      return {
        ...state,
        loadedList: uniq([
          ...(state?.loadedList || []),
          ...(action?.payload?.loadedList || []),
        ]),
        realIdsCorrelation: {
          ...(state?.realIdsCorrelation || {}),
          ...(action?.payload?.realIdsCorrelation || {}),
        },
        virtualIdsCorrelation: {
          ...(state?.virtualIdsCorrelation || {}),
          ...(action?.payload?.virtualIdsCorrelation || {}),
        },
        loadingList: without(
          state.loadingList,
          ...(action.payload.loadedList || []),
        ),
      };
    case TASKS_ACTION_TYPES.ADD_TASK_TO_INFINITE_LIST:
      if (action.payload.projectId !== state.projectId) {
        return state;
      }
      return {
        ...state,
        loadedList: uniq([
          ...(state?.loadedList || []),
          ...(action?.payload?.loadedList || []),
        ]),
        realIdsCorrelation: {
          ...(state?.realIdsCorrelation || {}),
          ...(action?.payload?.realIdsCorrelation || {}),
        },
        sections: action?.payload?.sections,
        virtualIdsCorrelation: {
          ...(state?.virtualIdsCorrelation || {}),
          ...(action?.payload?.virtualIdsCorrelation || {}),
        },
        loadingList: without(
          state.loadingList,
          ...(action.payload.loadedList || []),
        ),
      };
    case TASKS_ACTION_TYPES.REMOVE_PROJECT_FROM_TASK_SUCCESS:
    case TASKS_ACTION_TYPES.REMOVE_TASK_FROM_INFINITE_LIST: {
      if (!state?.sections || !Object.keys(state.sections).includes(action?.payload?.sectionId)) {
        return state;
      }
      const taskIdToRemove = action?.payload?.taskId;
      const virtualizedTaskId = state?.realIdsCorrelation?.[taskIdToRemove];
      const sectionIdToClean =
        action?.payload?.sectionId ||
        Object.keys(state?.sections || {}).find((sectionId) =>
          (state?.sections?.[sectionId]?.virtualizedIdList || []).includes(
            virtualizedTaskId,
          ),
        );

      if (!virtualizedTaskId) return state;

      return {
        ...state,
        loadedList: without(state?.loadedList || [], virtualizedTaskId),
        loadingList: without(state?.loadingList || [], virtualizedTaskId),
        virtualIdsCorrelation: omit(
          state?.virtualIdsCorrelation || {},
          virtualizedTaskId,
        ),
        realIdsCorrelation: omit(
          state?.realIdsCorrelation || {},
          taskIdToRemove,
        ),
        sections: sectionIdToClean
          ? {
              ...(state?.sections || {}),
              [sectionIdToClean]: {
                virtualizedIdList: without(
                  state?.sections?.[sectionIdToClean]?.virtualizedIdList || [],
                  virtualizedTaskId,
                ),
              },
            }
          : state?.sections,
      };
    }
    case TASKS_ACTION_TYPES.UPDATE_TASK_EVENT: {
      if (!state?.projectId) return state;

      const needToRemove =
        (action?.payload?.prevProjects || []).includes(state?.projectId) &&
        !Object.keys(
          action?.payload?.task?.projectAndSectionLabelList || {},
        ).includes(state?.projectId) &&
        state?.realIdsCorrelation?.[action?.payload?.taskId];
      const needToAdd =
        !(action?.payload?.prevProjects || []).includes(state?.projectId) &&
        Object.keys(
          action?.payload?.task?.projectAndSectionLabelList || {},
        ).includes(state?.projectId);
      const needToUpdate = needToRemove || needToAdd;

      if (!needToUpdate) return state;

      const virtualizedId =
        state?.realIdsCorrelation?.[action?.payload?.taskId] ||
        crypto.randomUUID();

      const updatedSections = { ...(state?.sections || {}) };

      if (needToAdd) {
        const sectionToUpdate =
          action?.payload?.task?.projectAndSectionLabelList?.[state?.projectId]
            ?.sectionId;
        updatedSections[sectionToUpdate] = {
          virtualizedIdList: uniq([
            virtualizedId,
            ...(updatedSections?.[sectionToUpdate]?.virtualizedIdList || []),
          ]),
        };
      } else {
        (action?.payload?.prevSections || []).forEach((sectionId) => {
          if (Object.keys(updatedSections).includes(sectionId)) {
            updatedSections[sectionId] = {
              virtualizedIdList: without(
                updatedSections?.[sectionId]?.virtualizedIdList || [],
                virtualizedId,
              ),
            };
          }
        });
      }

      return {
        ...state,
        loadedList: needToAdd
          ? uniq([...(state?.loadedList || []), virtualizedId])
          : without(state?.loadedList || [], virtualizedId),
        realIdsCorrelation: needToAdd
          ? {
              ...(state?.realIdsCorrelation || {}),
              [action?.payload?.taskId]: virtualizedId,
            }
          : omit(state?.realIdsCorrelation || {}, action?.payload?.taskId),
        sections: updatedSections,
        virtualIdsCorrelation: needToAdd
          ? {
              ...(state?.virtualIdsCorrelation || {}),
              [virtualizedId]: action?.payload?.taskId,
            }
          : omit(state?.virtualIdsCorrelation || {}, virtualizedId),
        loadingList: without(state?.loadingList, virtualizedId),
      };
    }
    case TASKS_ACTION_TYPES.INFINITE_LIST_ITEM_DRAG_N_DROP_ACTION: {
      if (action?.payload?.projectId !== state?.projectId) {
        return state;
      }

      return {
        ...state,
        sections: action?.payload?.sections,
      };
    }
    case TASKS_ACTION_TYPES.INFINITE_LIST_ITEM_SECTION_CHANGE_ACTION: {
      const fn = '[infiniteListData][INFINITE_LIST_ITEM_SECTION_CHANGE_ACTION]';
      const { fromSection, toSection, isSameSection, projectId, taskId } =
        action?.payload;

      if (isSameSection || projectId !== state?.projectId) {
        return state;
      }

      let virtualizedId = state?.realIdsCorrelation?.[taskId];

      if (!virtualizedId) {
        const revertedFromSectionIdList = (
          state?.sections?.[fromSection]?.virtualizedIdList || []
        ).reverse();

        virtualizedId = revertedFromSectionIdList.find(
          (id) => !(state?.loadedList || []).includes(id),
        );
      }

      if (!virtualizedId) {
        console.warn(`${fn}: Failed to get the virtualizedId for ${taskId}`);
        return state;
      }

      const updatedSections = { ...(state?.sections || {}) };
      let loadedList;

      if (fromSection) {
        updatedSections[fromSection] = {
          virtualizedIdList: without(
            updatedSections?.[fromSection]?.virtualizedIdList || [],
            virtualizedId,
          ),
        };
      }

      if (toSection) {
        const toSectionVirtualizedIdList =
          updatedSections?.[toSection]?.virtualizedIdList || [];
        updatedSections[toSection] = {
          virtualizedIdList: toSectionVirtualizedIdList.includes(virtualizedId)
            ? toSectionVirtualizedIdList
            : uniq([
                virtualizedId,
                ...(updatedSections?.[toSection]?.virtualizedIdList || []),
              ]),
        };
      }
      const isNextItemLoaded = (state?.loadedList || []).includes(
        ((state?.sections || [])[toSection]?.virtualizedIdList || [])[0],
      );

      if (isNextItemLoaded) {
        loadedList = uniq([...(state?.loadedList || []), virtualizedId]);
      } else {
        loadedList = without(state?.loadedList, virtualizedId);
      }

      return {
        ...state,
        sections: updatedSections,
        loadedList,
        realIdsCorrelation: {
          ...(state?.realIdsCorrelation || {}),
          [taskId]: virtualizedId,
        },
        virtualIdsCorrelation: {
          ...(state?.virtualIdsCorrelation || {}),
          [virtualizedId]: taskId,
        },
      };
    }
    case SECTIONS_ACTION_TYPES.ADD_SECTION_TO_VIRTUAL_LIST_SUCCESS: {
      const sectionId = action?.payload?._id;
      return {
        ...state,
        sections: {
          ...(state?.sections || {}),
          [sectionId]: {
            virtualizedIdList: [],
          },
        },
        loadedList: uniq([sectionId, ...(state?.loadedList || [])]),
      };
    }
    case SECTIONS_ACTION_TYPES.REMOVE_SECTION_SUCCESS: {
      const sectionIdToRemove = action?.payload;

      const virtualizedIdListToRemove =
        state?.sections?.[sectionIdToRemove]?.virtualizedIdList || [];

      return {
        ...state,
        loadedList: without(
          state?.loadedList,
          sectionIdToRemove,
          ...virtualizedIdListToRemove,
        ),
        realIdsCorrelation: omitBy(
          state?.realIdsCorrelation,
          (realIdCorrelation) =>
            virtualizedIdListToRemove.includes(realIdCorrelation),
        ),
        sections: omit(state?.sections, [sectionIdToRemove]),
        virtualIdsCorrelation: omit(state?.virtualIdsCorrelation, virtualizedIdListToRemove),
      };
    }
    // FIXME: [new tasks store] BE is messing order, commented action for now in order not to show jumping tasks
    // case TASKS_ACTION_TYPES.TASKS_REORDER_EVENT: {
    //   console.warn('TASKS_REORDER_EVENT', action.payload);
    //   return state;
    // }
    default: {
      return state;
    }
  }
};

export default infiniteListDataReducer;
